import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const LS_KEY = "danielle_memorial_entered";
const REAL_SRC = "/audio/danielles-song.mp3";
const PLACEHOLDER_SRC = "/audio/placeholder.wav";

/**
 * Opening splash for Danielle's memorial.
 * - Shows once per browser (localStorage flag).
 * - "Enter the memorial" fades the overlay away and starts playing the song
 *   softly in the background as visitors scroll.
 * - "Enter silently" dismisses without audio.
 * Uses a dedicated <audio> element created imperatively so returning visitors
 * (who skipped the overlay) can also toggle from a compact floating button.
 */
export default function MemorialEntry() {
    const [open, setOpen] = useState(false);
    const [audioSrc, setAudioSrc] = useState(REAL_SRC);
    const [playing, setPlaying] = useState(false);
    const [floatingVisible, setFloatingVisible] = useState(false);
    const audioRef = useRef(null);

    // Probe the real MP3, fall back to placeholder
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(REAL_SRC, {
                    method: "GET",
                    headers: { Range: "bytes=0-1" },
                });
                if (cancelled) return;
                const ct = (r.headers.get("content-type") || "").toLowerCase();
                const isAudio =
                    ct.startsWith("audio/") ||
                    ct === "application/octet-stream";
                if (!r.ok || ct.includes("html") || !isAudio) {
                    setAudioSrc(PLACEHOLDER_SRC);
                }
            } catch {
                if (!cancelled) setAudioSrc(PLACEHOLDER_SRC);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // On mount decide whether to show splash or the floating button
    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            if (!localStorage.getItem(LS_KEY)) setOpen(true);
            else setFloatingVisible(true);
        } catch {
            setOpen(true);
        }
    }, []);

    const startAudio = async () => {
        const a = audioRef.current;
        if (!a) return;
        try {
            a.volume = 0.32;
            await a.play();
            setPlaying(true);
        } catch {
            /* browser rejected — user can still press the floating button */
        }
    };

    const enter = async () => {
        try {
            localStorage.setItem(LS_KEY, new Date().toISOString());
        } catch {
            /* ignore */
        }
        setOpen(false);
        setFloatingVisible(true);
        // Slight delay so play() is inside the same user-gesture chain
        await startAudio();
    };

    const enterSilent = () => {
        try {
            localStorage.setItem(LS_KEY, new Date().toISOString());
        } catch {
            /* ignore */
        }
        setOpen(false);
        setFloatingVisible(true);
    };

    const toggle = async () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) {
            try {
                a.volume = 0.32;
                await a.play();
                setPlaying(true);
            } catch {
                /* ignore */
            }
        } else {
            a.pause();
            setPlaying(false);
        }
    };

    return (
        <>
            {/* Persistent audio element */}
            <audio
                ref={audioRef}
                src={audioSrc}
                preload="metadata"
                loop
                data-testid="memorial-audio"
            />

            {/* Entry splash */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-0 z-[100] flex items-center justify-center px-6"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Enter the memorial for Danielle"
                        data-testid="memorial-entry"
                    >
                        <div className="absolute inset-0 bg-neutral-950" />
                        <div
                            className="absolute inset-0 opacity-45 pointer-events-none"
                            style={{
                                backgroundImage: "url(/scenes/candle_flame.png)",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/60 via-neutral-950/40 to-neutral-950/80 pointer-events-none" />
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,transparent_30%,rgba(10,10,10,0.9)_100%)] pointer-events-none" />

                        <motion.div
                            initial={{ opacity: 0, y: 24, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{
                                duration: 1.2,
                                delay: 0.2,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="relative z-10 max-w-3xl w-full text-center text-neutral-100"
                        >
                            <div className="font-sans text-[10px] uppercase tracking-[0.4em] text-amber-300">
                                A Memorial
                            </div>
                            <h1 className="mt-6 font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.98] tracking-tight">
                                <span className="italic font-light">For</span>{" "}
                                Danielle
                            </h1>
                            <div className="mt-4 font-sans text-[10px] uppercase tracking-[0.4em] text-neutral-400 tabular">
                                Aug 5, 1993 &nbsp;—&nbsp; Oct 6, 2021
                            </div>

                            <p className="mt-10 sm:mt-14 max-w-xl mx-auto font-serif italic text-lg sm:text-xl text-neutral-300 leading-relaxed">
                                Her mother has kept every word of her story.
                                Enter quietly. Let her song carry you.
                            </p>

                            <button
                                type="button"
                                onClick={enter}
                                data-testid="enter-memorial-button"
                                className="group relative mt-12 sm:mt-16 inline-flex items-center gap-4 border border-neutral-200 px-10 py-5 font-sans text-xs uppercase tracking-[0.4em] text-neutral-50 hover:text-neutral-950 transition-colors duration-700"
                            >
                                <span className="absolute inset-0 origin-bottom scale-y-0 group-hover:scale-y-100 bg-neutral-50 transition-transform duration-700 ease-[cubic-bezier(0.77,0,0.175,1)]" />
                                <span className="relative flex items-center gap-4">
                                    <span
                                        aria-hidden="true"
                                        className="w-2 h-2 rounded-full bg-amber-300 group-hover:bg-amber-500 transition-colors duration-500"
                                    />
                                    <span>Enter the memorial</span>
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={enterSilent}
                                data-testid="enter-memorial-silent"
                                className="mt-8 block mx-auto font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 hover:text-neutral-200 transition-colors"
                            >
                                Enter silently
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating song toggle — small persistent control */}
            <AnimatePresence>
                {floatingVisible && !open && (
                    <motion.button
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.9 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        type="button"
                        onClick={toggle}
                        data-testid="floating-song-button"
                        aria-label={playing ? "Pause Danielle's song" : "Play Danielle's song"}
                        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60] group flex items-center gap-3 pl-4 pr-5 py-3 bg-neutral-950 text-neutral-50 border border-neutral-700 hover:border-amber-300 shadow-2xl font-sans text-[10px] uppercase tracking-[0.35em] transition-colors duration-500"
                    >
                        <span className="relative flex items-center justify-center w-4 h-4">
                            {playing ? (
                                <span className="flex items-end gap-[2px]">
                                    <span className="w-[3px] h-3 bg-amber-300 animate-[pulse_1s_ease-in-out_infinite]" />
                                    <span className="w-[3px] h-2 bg-amber-300 animate-[pulse_1s_ease-in-out_0.2s_infinite]" />
                                    <span className="w-[3px] h-3 bg-amber-300 animate-[pulse_1s_ease-in-out_0.4s_infinite]" />
                                </span>
                            ) : (
                                <svg
                                    width="10"
                                    height="12"
                                    viewBox="0 0 10 12"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    className="text-amber-300"
                                >
                                    <path d="M0 0 L10 6 L0 12 Z" />
                                </svg>
                            )}
                        </span>
                        <span>{playing ? "Playing · Danielle's Song" : "Play Danielle's Song"}</span>
                    </motion.button>
                )}
            </AnimatePresence>
        </>
    );
}
